import React, {useEffect, useState} from 'react';
import {withTranslation} from 'react-i18next';
import axios from 'axios';
import {Button} from 'primereact/button';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Dialog} from 'primereact/dialog';
import {InputText} from 'primereact/inputtext';
import {InputTextarea} from 'primereact/inputtextarea';
import {Dropdown} from 'primereact/dropdown';
import {Skeleton} from 'primereact/skeleton';
import {Message} from 'primereact/message';
import {Paginator} from 'primereact/paginator';

const EmployeeRecordsComponent = ({employeeId, kind, t, i18n}) => {
  const notes = kind === 'notes';
  const canWrite = (localStorage.getItem('roles') || '').includes('role_employee_write');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const url = `${process.env.REACT_APP_API_HOST}/api/v2/employee/${employeeId}/${kind}`;
  const headers = () => ({Authorization: `Bearer ${localStorage.getItem('userToken')}`, 'App-Network': localStorage.getItem('networkId')});
  useEffect(() => {
    let current = true;
    setLoading(true);
    axios.get(url, {headers: headers(), params: {page: page + 1}})
      .then(({data}) => { if (current) { setRows(data.data); setTotal(data.total || 0); } })
      .catch(e => { if (current) setError(e.response?.data?.detail || e.message); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [url, page, revision]);
  const save = async () => {
    setSaving(true); setError('');
    try {
      const data = notes ? {body: draft.body} : Object.fromEntries(['purpose', 'country', 'city', 'zip', 'address', 'valid_from', 'valid_until'].map(k => [k, draft[k] || null]));
      await axios({method: draft.id ? 'patch' : 'post', url: url + (draft.id ? `/${draft.id}` : ''), headers: headers(), data});
      setDraft(null); setRevision(r => r + 1);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };
  const field = (name, type = 'text', maxLength) => <div className="p-field p-fluid" key={name}>
    <label htmlFor={`employee-${name}`}>{t(`employeeRecords.${name}`)}</label>
    <InputText id={`employee-${name}`} type={type} maxLength={maxLength} value={draft[name] || ''} onChange={e => setDraft({...draft, [name]: e.target.value})}/>
  </div>;
  return <div className="p-mt-3">
    <div className="p-d-flex p-jc-between p-ai-center p-mb-2">
      <h3>{t(`employeeRecords.${kind}`)}</h3>
      {canWrite && <Button icon="pi pi-plus" label={t('employeeRecords.add')} className="p-button-success p-button-sm" onClick={() => {setError(''); setDraft(notes ? {body: ''} : {purpose: 'PERMANENT'});}}/>}
    </div>
    {notes && <p>{t('employeeRecords.noteHint')}</p>}
    {error && !draft && <Message severity="error" text={error}/>}
    {loading ? <Skeleton height="8rem"/> : <DataTable value={rows} dataKey="id" responsiveLayout="scroll" emptyMessage={t('employeeRecords.empty')}>
      {notes ? [
        <Column key="created" header={t('employeeRecords.created')} body={r => new Date(r.created_at).toLocaleString(i18n.language)}/>,
        <Column key="author" field="author" header={t('employeeRecords.author')}/>,
        <Column key="body" header={t('employeeRecords.body')} body={r => <span style={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{r.body}</span>}/>
      ] : [
        <Column key="purpose" header={t('employeeRecords.purpose')} body={r => t(`employeeRecords.${r.purpose}`)}/>,
        ...['country', 'zip', 'city', 'address', 'valid_from', 'valid_until'].map(k => <Column key={k} field={k} header={t(`employeeRecords.${k}`)}/>)
      ]}
      {canWrite && <Column body={r => (!notes || (r.can_edit && Date.now() < Date.parse(r.editable_until))) && <Button icon="pi pi-pencil" className="p-button-text" tooltip={t('employeeRecords.edit')} onClick={() => {setError(''); setDraft({...r});}}/>}/>}
    </DataTable>}
    {notes && <Paginator first={page * 20} rows={20} totalRecords={total} onPageChange={e => setPage(e.page)}/>}
    <Dialog header={t(`employeeRecords.${kind}`)} visible={draft !== null} style={{width: '36rem', maxWidth: '95vw'}} modal onHide={() => {if (!saving) setDraft(null);}}>
      {draft && <div>
        {error && <Message severity="error" text={error}/>}
        {notes ? <div className="p-field p-fluid"><InputTextarea rows={6} maxLength={10000} value={draft.body} onChange={e => setDraft({...draft, body: e.target.value})}/></div> : <>
          <div className="p-field p-fluid"><label>{t('employeeRecords.purpose')}</label><Dropdown value={draft.purpose} options={['PERMANENT', 'RESIDENCE', 'EDUCATIONAL'].map(value => ({value, label: t(`employeeRecords.${value}`)}))} onChange={e => setDraft({...draft, purpose: e.value})}/></div>
          {field('country', 'text', 2)}{field('zip', 'text', 6)}{field('city', 'text', 32)}{field('address', 'text', 180)}
          {field('valid_from', 'date')}{field('valid_until', 'date')}
        </>}
        <Button label={t('employeeRecords.save')} icon="pi pi-check" loading={saving} onClick={save}/>
      </div>}
    </Dialog>
  </div>;
};
export default withTranslation('common')(EmployeeRecordsComponent);
